import * as React from "react";
import { css } from "@emotion/react";
import f from "@/module/Utils/emotion";
import v from "@/module/Utils/variable";

// Components
import Modal from "@/components/Atoms/Modal";

// Images
import IconCross from "@/images/common/icon_cross";

// Data
import textData from "@/module/data/texts/start";
import Util from "@/module/Utils/Util";
import DefaultButton from "@/components/Molecules/DefaultButton";

interface ReportModalProps {
  isShow: boolean;
  onModalClose: () => void;
}

const ReportModal: React.FC<ReportModalProps> = (props) => {
  // Initialize
  const modal = textData.modal;

  //Style
  const ReportModalWrapStyle = css({
    width: "100%",
    height: "100%",
    [f.pc()]: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
  });

  const ReportModalScrollerStyle = css({
    overflow: "auto",
    pointerEvents: "auto",
    width: "100%",
    height: "100%",
    marginLeft: "auto",
    marginRight: "auto",
    backgroundColor: "#fff",
    [f.sp()]: {
      width: f.vw(650, v.spWidth),
      height: `${(1234 / 1334) * 100}%`,
      marginTop: f.vw(50, v.spWidth),
      paddingTop: f.vw(90, v.spWidth),
      paddingBottom: f.vw(90, v.spWidth),
      paddingLeft: f.vw(50, v.spWidth),
      paddingRight: f.vw(50, v.spWidth),
      borderRadius: f.vw(6, v.spWidth),
    },
    [f.pc()]: {
      width: f.vw(770),
      height: `80%`,
      // marginTop: f.vw(90),
      paddingTop: f.vw(90),
      paddingBottom: f.vw(90),
      paddingLeft: f.vw(100),
      paddingRight: f.vw(100),
      borderRadius: f.vw(6),
    },
  });

  const ModalCloseBtnStyle = css({
    position: "absolute",
    [f.sp()]: {
      top: f.vw(70, v.spWidth),
      right: f.vw(70, v.spWidth),
    },
    [f.pc()]: {
      // top: f.vw(120),
      top: "12%",
      right: f.vw(328),
      transition: "opacity 0.5s ease-out",
      "&:hover": {
        opacity: 0.6,
      },
    },
  });

  const ModalCloseIconStyle = css({
    display: "block",
    position: "relative",
    borderRadius: "50%",
    backgroundColor: "#000",
    [f.sp()]: {
      width: f.vw(60, v.spWidth),
      height: f.vw(60, v.spWidth),
    },
    [f.pc()]: {
      width: f.vw(60),
      height: f.vw(60),
    },
    "&:after": {
      content: "''",
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      backgroundImage: `url(${IconCross("#fff")})`,
      backgroundSize: "99% 99%",
      backgroundPosition: "top left",
      backgroundRepeat: "no-repeat",
      [f.sp()]: {
        width: f.vw(19, v.spWidth),
        height: f.vw(19, v.spWidth),
      },
      [f.pc()]: {
        width: f.vw(19),
        height: f.vw(19),
      },
    },
  });

  const tableListStyle = css({
    [f.pc()]: {},
    "& + &": {
      [f.pc()]: {
        marginTop: f.vw(40),
      },
      [f.sp()]: {
        marginTop: f.vw(60, v.spWidth),
      },
    },
  });

  const langStyle = () => {
    if (process.env.GATSBY_LANG === "ja") {
      return {
        padding: `${f.vw(10, v.spWidth)} ${f.vw(25, v.spWidth)} ${f.vw(
          3,
          v.spWidth
        )} ${f.vw(25, v.spWidth)}`,
      };
    } else if (process.env.GATSBY_LANG === "en") {
      return {
        padding: `${f.vw(10, v.spWidth)} ${f.vw(25, v.spWidth)} ${f.vw(
          10,
          v.spWidth
        )} ${f.vw(25, v.spWidth)}`,
      };
    }
  };

  const tableTeamStyle = css({
    display: "inline-block",
    lineHeight: 1,
    border: "solid 2px #D1D1D1",
    [f.sp()]: {
      borderRadius: f.vw(40, v.spWidth),
      border: "solid 1px #D1D1D1",
      ...langStyle(),
    },
    [f.pc()]: {
      padding: `${f.vw(10)} ${f.vw(25)}`,
      borderRadius: f.vw(30),
      fontSize: f.vw(16),
    },
  });

  const tableDescriptionStyle = css({
    ...f.regularFont(),
    [f.sp()]: {
      marginTop: f.vw(18, v.spWidth),
      fontSize: f.vw(28, v.spWidth),
      ...Util.insertStyle("en", { letterSpacing: "0.07em", lineHeight: "1.5" }),
    },
    [f.pc()]: {
      marginTop: f.vw(20),
      fontSize: f.vw(18),
      lineHeight: 32 / 18,
    },
  });

  const closeButtonTextStyle = css({
    marginLeft: "auto",
    marginRight: "auto",
    [f.sp()]: {
      width: f.vw(428, v.spWidth),
      height: f.vw(120, v.spWidth),
      marginTop: f.vw(60, v.spWidth),
    },
    [f.pc()]: {
      width: f.vw(220),
      height: f.vw(62),
      marginTop: f.vw(45),
      transition: "opacity 0.5s ease-out",
      "&:hover": {
        opacity: 0.6,
      },
    },
  });

  // Logic
  const createModalContents = () => {
    const dom = [];
    Object.keys(modal).forEach((key) => {
      if (key !== "close") {
        dom.push(
          <dl key={key} css={tableListStyle}>
            <dt css={tableTeamStyle}>{modal[key].title}</dt>
            <dd css={tableDescriptionStyle}>{modal[key].description}</dd>
          </dl>
        );
      }
    });
    return dom;
  };

  //LifeCycle

  return (
    <>
      <Modal isShow={props.isShow}>
        <div css={ReportModalWrapStyle}>
          <div css={ReportModalScrollerStyle}>
            <button
              type={"button"}
              css={ModalCloseBtnStyle}
              onClick={props.onModalClose}
            >
              <span css={ModalCloseIconStyle} />
            </button>
            {createModalContents()}
            <div css={closeButtonTextStyle}>
              <DefaultButton
                BgColor={"black"}
                icon={"cross"}
                hover={false}
                radius={60}
              >
                <button type={"button"} onClick={props.onModalClose}>
                  {modal.close}
                </button>
              </DefaultButton>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ReportModal;
